import { useOwningOrg, useUser } from '@cibo/profile'
import { useProgram, useProgramStats } from '@cibo/programs'
import { useFigures } from '@cibo/ui'
import { Box, Chip, LinearProgress, Paper, Stack, Typography, useTheme } from '@mui/material'
import numeral from 'numeral'
import { useTranslation } from 'react-i18next'
import { ProgramTargets } from './ProgramTargets'
import { parseEnrollmentNumbers, programOrgLimits } from './utils'

interface ProgramStatsProps {
  programId: string
}

// complexity reduction would require unnecessary gymnastics.
// eslint-disable-next-line
export const ProgramStats = ({ programId }: ProgramStatsProps) => {
  const { t } = useTranslation('impact/ProgramStats')
  const { data: userData, isLoading } = useUser()
  const {
    palette: { success, action },
  } = useTheme()
  const { acres, fields } = useFigures()
  const stats = useProgramStats({})

  const program = useProgram({ programId })
  const programStats = stats.data?.programs.find(s => s.programId === programId)
  const { data: owningOrg } = useOwningOrg()
  const orgLimits = programOrgLimits(
    program.data?.ledger.capsPerOrg,
    owningOrg,
    programStats?.perOrgLimit
  )

  if (
    !program.data ||
    !programStats ||
    isLoading ||
    !userData ||
    userData.user?.persona === 'grower'
  ) {
    return null
  }

  const isOpen = program.data.enrollmentWindow.isEnrolling

  const {
    availableNumber,
    enrolledCount,
    enrolledPercent,
    inProgressCount,
    isAcres,
    isFields,
    orgTargetReducedBy,
    orgTargetReducedByPercent,
    pipelineCount,
    inOrgWithoutLimit,
  } = parseEnrollmentNumbers({
    orgLimits,
    program: program.data,
    programStats,
    orgId: userData.orgId,
  })

  const displayGlobalLimit =
    (userData.user?.persona === 'admin' || !inOrgWithoutLimit) &&
    !!Object.keys(programStats.globalLimit.max)[0]

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="h6">{t('programSummary')}</Typography>
              <Typography variant="subtitle1">
                {displayGlobalLimit && (isFields ? t('fields') : isAcres ? t('acres') : null)}
              </Typography>
            </Stack>
            <Chip
              size="small"
              color={isOpen ? 'info' : 'error'}
              label={isOpen ? t('openEnrollment') : t('closed')}
            />
          </Stack>
          {displayGlobalLimit && (
            <Stack spacing={1.5}>
              <Stack direction="row">
                <ProgressBar color={success.dark} percent={enrolledPercent} />
                <ProgressBar
                  color={success.light}
                  percent={100 - enrolledPercent - orgTargetReducedByPercent}
                />
                {orgTargetReducedBy > 0 && (
                  <ProgressBar
                    color={action.disabledBackground}
                    percent={orgTargetReducedByPercent}
                  />
                )}
              </Stack>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <ProgressBarLegend
                  label={t('enrolled')}
                  color={success.dark}
                  quantity={enrolledCount}
                />
                <ProgressBarLegend
                  label={t('available')}
                  color={success.light}
                  quantity={availableNumber}
                />

                {orgTargetReducedBy > 0 && (
                  <ProgressBarLegend
                    label={t('noLongerAvailable')}
                    color={action.disabledBackground}
                    quantity={orgTargetReducedBy}
                  />
                )}
              </Stack>
              {orgTargetReducedBy > 0 && (
                <Typography variant="caption">
                  {t('noLongerAvailableExplainer', { count: availableNumber })}
                </Typography>
              )}
            </Stack>
          )}
          <Stack spacing={1.5}>
            <Typography fontWeight="bold" data-testid="pipeline">
              {t('pipeline', { context: program.data.engageType })}
            </Typography>
            <StatusIndicator
              data-testid="progress-pipeline"
              label={t('inProgress')}
              count={isAcres ? acres(inProgressCount, true) : fields(inProgressCount)}
              percent={(inProgressCount / pipelineCount) * 100}
            />
            <StatusIndicator
              data-testid="enrolled-pipeline"
              label={t('enrolled')}
              count={isAcres ? acres(enrolledCount, true) : fields(enrolledCount)}
              percent={(enrolledCount / pipelineCount) * 100}
            />
          </Stack>
        </Stack>
      </Paper>
      <ProgramTargets programId={programId} />
    </Stack>
  )
}

const ProgressBar = ({ percent, color }: { percent: number; color: string }) => (
  <Box
    sx={{
      background: color,
      borderRadius: '4px',
      height: '8px',
      marginInline: '2px',
      marginInlineStart: 0,
      width: `${percent}%`,
    }}
  />
)

const ProgressBarLegend = ({
  label,
  color,
  quantity,
}: {
  label: string
  color: string
  quantity: number
}) => (
  <Stack>
    <Typography variant="caption" color="text.primary">
      {label}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          background: color,
          width: '12px',
          height: '12px',
          borderRadius: '4px',
        }}
      />
      <Typography variant="caption">{numeral(quantity).format('0,0')}</Typography>
    </Stack>
  </Stack>
)

const StatusIndicator = ({
  label,
  percent,
  count,
  ...rest
}: {
  label: string
  percent: number
  count: string
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={1} {...rest}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="50%">
      <Typography>{label}</Typography>
      <Typography variant="caption">{count}</Typography>
    </Stack>
    <Stack alignItems="center" direction="row" width="50%">
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" value={percent} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(percent)}%`}</Typography>
      </Box>
    </Stack>
  </Stack>
)
